import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useRef } from "react";
import NexusCanvas from "@/components/NexusCanvas";
import { MaskedLine } from "@/components/Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      className="hero-radial relative flex min-h-[100svh] items-center overflow-hidden"
      data-testid="hero-section"
    >
      <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <NexusCanvas />
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-40 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-blue-400"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block h-1.5 w-1.5 animate-pulse-dot rounded-full bg-cyan-400" />
          LogiTech Nexus · Software Development Company
        </motion.p>

        <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.35}>Technology Built</MaskedLine>
          <MaskedLine delay={0.5}>
            Around <span className="text-accent-gradient">Your Business.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
          data-testid="hero-subtitle"
        >
          LogiTech Nexus designs and develops modern software solutions that help
          businesses operate smarter, automate workflows, connect systems, and
          scale with confidence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-start-project-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_32px_rgba(59,130,246,0.4)] transition-[background-color,box-shadow,transform] duration-300 hover:bg-blue-500 hover:shadow-[0_0_44px_rgba(59,130,246,0.55)] active:scale-[0.98]"
          >
            Start a Project
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-explore-services-btn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-[border-color,background-color] duration-300 hover:border-blue-500/60 hover:bg-slate-800/40"
          >
            Explore Our Services
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-hidden="true"
      >
        <ChevronDown className="animate-bounce text-slate-500" size={20} />
      </motion.div>
    </section>
  );
}
